import React from "react";
import { ShieldCheck, ArrowRight, Server, HardDrive } from "lucide-react";

/**
 * Compact source → destination visualization.
 * Animated dashed line represents encrypted peer-to-peer transfer.
 */
export default function FlowVisualization({
    source,
    destination,
    status = "idle", // idle | running | paused | succeeded | failed
    speedMbps,
    progressPct,
    mode = "one_shot",
}) {
    const animate = status === "running";
    const lineColor =
        status === "succeeded"
            ? "hsl(var(--success))"
            : status === "failed" || status === "completed_with_errors"
              ? "hsl(var(--destructive))"
              : "hsl(var(--accent))";

    return (
        <div
            className="relative border border-border bg-surface p-5"
            data-testid="flow-visualization"
        >
            <div className="flex items-center justify-between gap-4">
                <AgentNode icon={Server} label="Source" name={source?.name} sub={source?.hostname} side="left" />
                <div className="relative flex-1">
                    <svg viewBox="0 0 400 60" className="h-12 w-full" preserveAspectRatio="none">
                        <defs>
                            <marker id="arrowhead" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                                <polygon points="0 0, 8 4, 0 8" fill={lineColor} />
                            </marker>
                        </defs>
                        {/* static rail */}
                        <line x1="0" y1="30" x2="400" y2="30" stroke="hsl(var(--border))" strokeWidth="1" />
                        {/* active flow */}
                        <line
                            x1="0"
                            y1="30"
                            x2="400"
                            y2="30"
                            stroke={lineColor}
                            strokeWidth="2"
                            strokeDasharray="8 6"
                            markerEnd="url(#arrowhead)"
                            style={
                                animate
                                    ? { animation: "flow-dash 1.1s linear infinite" }
                                    : undefined
                            }
                        />
                    </svg>
                    <style>{`@keyframes flow-dash { to { stroke-dashoffset: -28; } }`}</style>
                    <div className="pointer-events-none absolute inset-x-0 -top-4 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        <ShieldCheck size={11} className="text-[hsl(var(--success))]" />
                        AES-256-GCM · SHA-256 · {mode.replace("_", " ")}
                    </div>
                    <div className="pointer-events-none absolute inset-x-0 -bottom-2 flex items-center justify-center gap-3 text-[11px]">
                        {typeof progressPct === "number" && (
                            <span className="font-mono tabular-nums text-foreground" data-testid="flow-progress">
                                {progressPct.toFixed(1)}%
                            </span>
                        )}
                        {typeof speedMbps === "number" && speedMbps > 0 && (
                            <span
                                className="font-mono tabular-nums text-[hsl(var(--accent))]"
                                data-testid="flow-speed"
                            >
                                {speedMbps.toFixed(1)} MB/s
                            </span>
                        )}
                        <span className="font-mono uppercase tracking-widest text-muted-foreground">
                            {status}
                        </span>
                    </div>
                </div>
                <AgentNode
                    icon={HardDrive}
                    label="Destination"
                    name={destination?.name}
                    sub={destination?.hostname}
                    side="right"
                />
            </div>
            <div className="mt-4 border-t border-border pt-3 text-[11px] text-muted-foreground">
                Payload travels directly agent-to-agent. <span className="text-foreground">Never</span> through MoovBytes
                control plane.
            </div>
        </div>
    );
}

function AgentNode({ icon: Icon, label, name, sub, side }) {
    return (
        <div
            className={`flex w-40 flex-col items-center text-center ${side === "right" ? "md:items-end md:text-right" : "md:items-start md:text-left"}`}
        >
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <Icon size={12} />
                {label}
            </div>
            <div className="mt-1 font-display text-lg font-bold leading-tight">{name || "—"}</div>
            <div className="truncate font-mono text-[10px] text-muted-foreground max-w-full">{sub || "—"}</div>
        </div>
    );
}
